<template>
  <div
    ref="library-episodes"
    class="library-episodes"
    data-test="library-episodes">
    <div
      v-if="isLoggedIn">
      <loader
        v-show="!episodes_loaded && !episodes.length"
        center
        label="Fetching Episodes ..."/>
      <div class="container">
        <el-row
          type="flex"
          justify="space-between"
          align="middle"
          class="top-bar">
          <p
            class="theme-primary-header"
            data-test="count-episode">{{ totalEpisodes }} Episode{{ totalEpisodes > 1 || !totalEpisodes ? 's' : '' }}</p>
          <div
            v-if="episodes.length"
            class="filter-select">
            <el-dropdown
              trigger="click"
            >
              <span class="el-dropdown-link">
                {{ filter_name }}
                <i class="el-icon-arrow-down el-icon--right" />
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  v-for="elem in select_filters"
                  :key="elem.val"
                  @click.native="onSelect(elem.val)"
                >
                  {{ elem.name }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </el-row>
      </div>
      <!-- Empty Shows Box -->
      <empty-shows
        v-show="!episodes.length && episodes_loaded"
        option="playlist"
      />
      <!-- Display Episodes-->
      <div
        v-if="episodes.length"
        class="draggable-container">
        <draggable
          v-model="episodes"
          :options="draggableOptions"
          tag="ul"
          data-test="list-episodes"
          class="list-episodes"
          handle=".drag-button"
          @start="onEpisodeSortStart($event)"
          @end="onEpisodeSortEnd($event)"
        >
          <li
            v-for="(item, index) in episodes"
            :key="item.uuid"
            :index="index"
            data-test="episode"
            class="draggable-episode">
            <card-list
              :index="index"
              :shows="item"
              :podcast="item.season.podcast"
              :toggle-details="item.show_details"
              :draggable-icon="true"
              :is-exclusive="item.season && item.season.podcast && item.season.podcast.is_exclusive"
              :is-free="(item.season && item.season.podcast && item.season.podcast.is_exclusive) && !item.is_exclusive"
              :season_no="item.season && item.season.number"
              :episode_no="item.episode_number"
              :bucket="bucket"
              playlist-type="my-episodes"
              @deleted="deleteEpisode($event,index)"
              @details="resizePlaceholder"
            />
          </li>
        </draggable>
        <loader
          v-show="episodes.length && !episodes_loaded && !no_more_episodes"
          :with-margin="true"/>
      </div>
    </div>
  </div>
</template>
<script>
import * as log from 'loglevel';
import draggable from 'vuedraggable';
import Episode from '../../services/Episode';
import User from '../../services/User';
import SegmentEvent from '../../services/PlayerEvent';
import ScrollMixin from '../../shared/scrollMixin';
import PageMixin from '../../shared/pageMixin';

export default {
  name: 'Shows',
  components: {
    draggable,
    'card-list': () => import('../../components/CardList'),
    'empty-shows': () => import('../../components/EmptyShows'),
    loader: () => import('../../components/Loader'),
  },
  mixins: [ScrollMixin, PageMixin],
  data() {
    return {
      pageName: 'My Playlist',
      episode_service: new Episode(),
      user_service: new User(),
      eventProvider: new SegmentEvent(),
      episodes: [],
      scroll_threshold: 200, // this is percentage
      isLoggedIn: this.$store.getters.loggedIn,
      page: 0,
      show_infinite_scroll: false,
      no_more_episodes: false,
      episodes_loaded: false,
      filter_value: null,
      filter_name: 'Sort By',
      show_placeholder: false,
      show_details: false,
      limit: 25,
      totalEpisodes: 0,
      select_filters: [
        {
          name: 'Position',
          val: 1,
          meth: () => {
            this.episodes.sort((a, b) => a.position - b.position);
          },
        },
        {
          name: 'Newest',
          val: 2,
          meth: () => {
            this.episodes.sort((a, b) => new Date(b.released_at) - new Date(a.released_at));
          },
        },
        {
          name: 'Oldest',
          val: 3,
          meth: () => {
            this.episodes.sort((a, b) => new Date(a.released_at) - new Date(b.released_at));
          },
        },
        {
          name: 'Shortest',
          val: 4,
          meth: () => {
            this.episodes.sort((a, b) => a.runtime - b.runtime);
          },
        },
        {
          name: 'Longest',
          val: 5,
          meth: () => {
            this.episodes.sort((a, b) => b.runtime - a.runtime);
          },
        },
      ],
      selected_filter_value: -1,
      bucket: {},
      draggableOptions: {
        animation: 300,
        ghostClass: 'ghost',
        chosenClass: 'chosen',
        dragClass: 'drag',
        delay: 0,
        scrollSensitivity: 100,
        scrollSpeed: 10,
        scroll: true,
        direction: 'horizontal',
        forceFallback: true,
      },
    };
  },
  beforeMount() {
    this.parentSelector = 'library-episodes';
    this.childSelector = '.library-episodes .show-card-list.is-visible';
  },
  updated() {
    this.resizePlaceholder();
  },
  mounted() {
    if (!this.isLoggedIn) return;
    this.getEpisodesFromLibrary();
    // this.infiniteScrollPlaylist();
    this.bucket = {
      bucket_uuid: null,
      name: 'Episodes',
      bucket_type: 'episodes',
      position: 0,
      items_count: this.episodes.length,
      media_size: 'episode_details',
    };
  },
  beforeDestroy() {
    this.removeScrollEvent();
  },
  methods: {
    getEpisodesFromLibrary() {
      this.episodes_loaded = false;
      if (this.no_more_episodes) {
        this.episodes_loaded = true;
        return;
      }
      this.page++;
      this.show_infinite_scroll = true;
      this.episode_service.getEpisodesFromLibrary(this.limit, this.page)
        .then((tempEpisodes) => {
          if (tempEpisodes && tempEpisodes.episodes.length < 25) {
            this.no_more_episodes = true;
            this.show_infinite_scroll = false;
            this.episodes_loaded = true;
          }
          this.totalEpisodes = tempEpisodes.total;
          if (!tempEpisodes.episodes.length) return;
          this.mapEpisodes(tempEpisodes.episodes);
          if (this.selected_filter_value !== -1) {
            Object.call(this.select_filters[this.selected_filter_value - 1].meth());
          } else {
            this.sortByPosition();
          }
          this.episodes_loaded = true;
          this.show_infinite_scroll = false;
          this.$store.dispatch('setPlaylist', this.episodes);
          setTimeout(() => {
            this.infiniteScrollPlaylist();
          }, 1000);
        })
        .catch((err) => {
          log.error(err.message);
          this.no_more_episodes = true;
          this.show_infinite_scroll = false;
          this.episodes_loaded = true;
        });
    },
    mapEpisodes(episodes) {
      // progress: item.episode.episode_listen ? item.episode.episode_listen.elapsed : 0,
      // total_runtime: item.episode.episode_listen ? item.episode.episode_listen.runtime : 0,
      // show_details: false,
      episodes.forEach((item) => {
        this.episodes.push({
          ...item.episode,
          progress: 0,
          record_id: item.uuid,
          position: item.position,
          display_image_url: item.episode.display_image_url || item.episode.season.podcast.display_image_url,
          in_library: true,
          height: 170,
          toggleDraggable: false,
        });
      });
      this.contentList = this.episodes;
    },
    infiniteScrollPlaylist() {
      document.addEventListener('scroll', this.infiniteScrollPlaylistCallback);
    },
    infiniteScrollPlaylistCallback() {
      const scrollTop = this.isSafariMobile ? document.body.scrollTop : document.documentElement.scrollTop;
      const heightLimit = scrollTop + window.innerHeight + this.scroll_threshold >= document.documentElement.scrollHeight;
      if (heightLimit && this.episodes_loaded) {
        this.getEpisodesFromLibrary();
      }
    },
    resizePlaceholder() {
      // Resize Placeholders
      try {
        const elem = document.querySelectorAll('.draggable-container .show-card-list');
        if (!elem || !elem.length) {
          return;
        }
        for (let i = 0; i < elem.length; i++) {
          this.episodes[i].height = elem[i].clientHeight;
        }
      } catch (ex) {
        // console.log(`Exception: ${ex.message}`);
      }
    },
    onEpisodeSortStart() {
      this.resizePlaceholder();
    },
    onEpisodeSortEnd(event) {
      this.episodes[event.oldIndex].toggleDraggable = false;
      this.resizePlaceholder();
      if (event.oldIndex !== event.newIndex) {
        this.updateEpisodePosition(this.episodes[event.newIndex], event.newIndex + 1);
      }
    },
    sortByPosition() {
      this.episodes.sort((a, b) => a.position - b.position);
    },
    deleteEpisode(id, index) {
      if (!id) return;
      this.episodes.splice(index, 1);
      this.totalEpisodes = this.totalEpisodes - 1;
    },
    updateEpisodePosition(userEpisode, pos) {
      this.episode_service.updateEpisodePosition(userEpisode.record_id, pos)
        .then((res) => {
          if (res && res.success) this.eventProvider.triggerEpisodeReorderEvent(userEpisode.uuid, res.new_position, res.position);
        })
        .catch((err) => { log.warn(err.message); });
    },
    filterSelect(val) {
      this.selected_filter_value = val;
      const ind = this.select_filters.findIndex(elem => this.filter_value === elem.val);
      if (ind > -1) {
        this.filter_name = this.select_filters[ind].name;
        Object.call(this.select_filters[ind].meth());
      }
      log.info(this.episodes);
    },
    removeScrollEvent() {
      const { body } = document;
      body.style = {};
      document.removeEventListener('scroll', this.infiniteScrollPlaylistCallback);
    },
    onSelect(value) {
      this.filter_value = value;
      this.filterSelect();
    },
  },
};
</script>
<style lang="scss">
@import '../../assets/scss/variables.scss';
.el-select-dropdown__item {
  height: 30px !important;
  line-height: 30px !important;
}
.library-episodes {
  min-height: calc(100vh - 347px);
  .top-bar {
    margin: 35px 0px;
    >p {
      font-size: 36px;
      line-height: 40px;
      font-weight: bold;
      color: $header-color;
    }
  }
  .filter-select {
    .el-select {
      width: 130px;
      cursor: pointer;
      .el-input {
        .el-input__inner {
          border-radius: 20px !important;
          color: $black !important;
          border: 1px solid $black !important;
          font-weight: 500 !important;
          padding: 5px 15px 5px 30px;
          font-size: 14px !important;
          line-height: 20px !important;
          height: 32px !important;
        }
        .el-input__inner::placeholder {
          overflow: visible;
          color: $black !important;
        }
        .el-input__suffix {
          i {
            font-size: 10px;
            color: $black !important;
            line-height: 0 !important;
          }
        }
      }
    }
    .el-select::before {
      content: "";
      position: absolute;
      display: block;
      top: 11px;
      left: 11px;
      background-size: contain;
      z-index: 1;
      width: 15px;
      height: 15px;
      background: url(../../assets/images/ic_sort_icon.svg) no-repeat;
    }
    .el-dropdown {
      cursor: pointer;
      display: flex;
      flex-direction: column;
      height: 32px;
      .el-dropdown-link {
        border-radius: 20px !important;
        color: #fff !important;
        border: 1px solid #fff !important;
        font-weight: 500;
        padding: 8px 10px 6px 30px;
        font-size: 14px !important;
        height: 32px;
        line-height: 4px;
      }
    }
    .el-dropdown::before {
      content: "";
      position: absolute;
      z-index: 1;
      background: url(../../assets/images/ic_sort_icon.svg) no-repeat;
      width: 13px;
      height: 13px;
      left: 12px;
      top: 12px;
      background-size: contain;
    }
  }
  .draggable-container {
    position: relative;

    .list-episodes {
      padding-left: 0;
      // height: 60vh;
      overflow-x: hidden;
      overflow-x: hidden;

      @media (max-height: 800px) {
        // height: 55vh;
      }

      @media (max-height: 750px) {
        // height: 50vh;
      }
    }

    .placeholder-container {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      z-index: -3;
      .single-placeholder {
        background-color: $xxlight-gray;
        display: flex;
        justify-content: center;
        align-items: center;
        .placeholder {
          background-color: #fff;
          border: 2px solid $xlight-gray;
          border-radius: 4px;
          padding: 0.75rem;
          width: 90%;
          p {
            text-align: center;
            color: $light-gray;
            font-size: 1rem;
          }
        }
      }
    }

    .draggable-episode {
      list-style-type: none;

      &.ghost {
        height: 155px;
        box-sizing: border-box;
        .show-card-list {
          .draggable-element {
            display: none;
            visibility: hidden;
            height: 0;
          }
        }
      }

      &.chosen {
        padding: 30px;
        .show-card-list {
          display: inline-block;
          background: white;
          visibility: visible;
          padding: 0px;
          .draggable-element {
            height: 80px;
            visibility: visible;
          }
        }
        .main-content {
          display: none;
        }
      }

      &.sortable-fallback {
        opacity: 1;
        .show-card-list {
          display: inline-block;
          background: white;
          padding: 0px;
          opacity: 1;
          border-radius: 20px;
        }
      }
    }
  }
}

</style>
